import type { TGameSite, TUpsellQualificationRuntime, TUpsellQualifier } from './types';
import { qualifyRwfVenomousAbyssCoverage } from './upsells/rwfVenomousAbyssCoverageQualification';
import { qualifyTalentBuildsCrossPromo } from './upsells/talentBuildsCrossPromoQualification';

export type TUpsellId =
  | 'awc-midnight-cup-3'
  | 'break-the-meta-feedback-survey'
  | 'break-the-meta-midnight-season-1'
  | 'elekk-giveaway'
  | 'mdi-midnight-groups'
  | 'mdi-midnight-season-finals'
  | 'mdi-replay-race-midnight-season-1'
  | 'midnight-addons'
  | 'midnight-affixes'
  | 'midnight-season-1-recap'
  | 'most-of-rio-midnight'
  | 'rwf-venomous-abyss-coverage'
  | 'talent-builds-cross-promo';

type TUpsellConfig = {
  id: TUpsellId;
  startAt?: string;
  endAt?: string;
  /** Restrict this upsell to specific game sites. Omit to show on all of them. */
  games?: TGameSite[];
  /**
   * Relative share of the seed rotation vs the other active upsells (default 1).
   * When an id has multiple entries, the first active entry's weight wins.
   */
  weight?: number;
  /** Campaign-owned eligibility. Qualified candidates enter the page's upsell sequence with the returned data. */
  qualify?: TUpsellQualifier;
};

export type TUpsellQualifications = ReadonlyMap<TUpsellId, unknown>;

const MIDNIGHT_TIME_TRIALS_ENDS_AT = '2026-04-21T01:00:00Z';
const ELEKK_GIVEAWAY_STARTS_AT = '2026-05-11T00:00:00Z';
const ELEKK_GIVEAWAY_ENDS_AT = '2026-05-23T00:00:00Z';
const AWC_MIDNIGHT_GRAND_FINALS_STARTS_AT = '2026-06-04T17:00:00Z';
const AWC_MIDNIGHT_GRAND_FINALS_ENDS_AT = '2026-06-08T04:00:00Z';
const MDI_MIDNIGHT_GROUP_A_STARTS_AT = '2026-05-07T17:00:00Z';
const MDI_MIDNIGHT_GROUP_A_ENDS_AT = '2026-05-11T02:00:00Z';
const MDI_MIDNIGHT_GROUP_B_STARTS_AT = '2026-05-14T17:00:00Z';
const MDI_MIDNIGHT_GROUP_B_ENDS_AT = '2026-05-18T02:00:00Z';
const MDI_MIDNIGHT_GROUP_C_STARTS_AT = '2026-05-21T17:00:00Z';
const MDI_MIDNIGHT_GROUP_C_ENDS_AT = '2026-05-25T02:00:00Z';
const MDI_REPLAY_RACE_MN_S1_STARTS_AT = '2026-05-26T20:00:00Z'; // 1 PM PDT (UTC-7)
const MDI_REPLAY_RACE_MN_S1_ENDS_AT = '2026-06-13T06:59:00Z'; // 11:59 PM PDT Fri Jun 12 (June is PDT/UTC-7)
// Season Finals upsell runs for the event window only (bracket start -> end); no pre-event countdown.
export const MDI_MIDNIGHT_SEASON_FINALS_ENDS_AT = '2026-06-15T02:00:00Z';
const MDI_MIDNIGHT_SEASON_FINALS_STARTS_AT = '2026-06-12T17:00:00Z';
// Break the Meta - Midnight S1: registration banner goes live on deploy. The component also uses
// these event dates to switch its copy and drive its live countdown.
const BTM_MN_S1_UPSELL_STARTS_AT = '2026-06-30T00:00:00Z';
export const BTM_MN_S1_EVENT_START_AT = '2026-07-14T15:00:00Z';
export const BTM_MN_S1_EVENT_END_AT = '2026-07-21T15:00:00Z';
// MN1 recap: goes live on deploy; runs through the season wrap-up window into early MN S2
// (S2 starts Aug 18/19) — same lifecycle as the TWW expansion recap upsell (Feb 20 → Mar 11).
const MN1_RECAP_UPSELL_STARTS_AT = '2026-08-06T00:00:00Z';
const MN1_RECAP_UPSELL_ENDS_AT = '2026-09-01T00:00:00Z';
// RWF The Venomous Abyss: banner goes live with the MN S2 deploy (race-eve hype — Mythic
// opens with the week-1 reset this tier), the component flips its copy to LIVE at US mythic
// open (race start — keep in sync with MIDNIGHT_SEASON2_MYTHIC_DATES.us), and it leaves
// rotation two weeks into the race.
const RWF_VENOMOUS_ABYSS_UPSELL_STARTS_AT = '2026-08-17T00:00:00Z';
export const RWF_VENOMOUS_ABYSS_RACE_STARTS_AT = '2026-08-18T15:00:00Z';
const RWF_VENOMOUS_ABYSS_UPSELL_ENDS_AT = '2026-09-01T15:00:00Z';
// BTM Feedback Survey: live on deploy, ends when the survey closes (Sep 5 16:00 UTC).
const BTM_FEEDBACK_SURVEY_STARTS_AT = '2026-08-14T00:00:00Z';
const BTM_FEEDBACK_SURVEY_ENDS_AT = '2026-09-05T16:00:00Z';

const ACTIVE_SHARED_UPSELLS: TUpsellConfig[] = [
  {
    id: 'talent-builds-cross-promo',
    games: ['wowretail'],
    qualify: qualifyTalentBuildsCrossPromo
  },
  {
    id: 'rwf-venomous-abyss-coverage',
    startAt: RWF_VENOMOUS_ABYSS_UPSELL_STARTS_AT,
    endAt: RWF_VENOMOUS_ABYSS_UPSELL_ENDS_AT,
    games: ['wowretail'],
    // Lead promo through the race window; the survey below runs at 1/3 this frequency.
    weight: 3,
    qualify: qualifyRwfVenomousAbyssCoverage
  },
  {
    id: 'break-the-meta-feedback-survey',
    startAt: BTM_FEEDBACK_SURVEY_STARTS_AT,
    endAt: BTM_FEEDBACK_SURVEY_ENDS_AT,
    games: ['wowretail'],
    // Was weight 3 while it had the stage to itself; dropped to 1 when the RWF Venomous
    // Abyss upsell (weight 3) launched so the survey runs at 1/3 the RWF frequency.
    weight: 1
  },
  {
    id: 'midnight-season-1-recap',
    startAt: MN1_RECAP_UPSELL_STARTS_AT,
    endAt: MN1_RECAP_UPSELL_ENDS_AT,
    games: ['wowretail']
  },
  {
    id: 'break-the-meta-midnight-season-1',
    startAt: BTM_MN_S1_UPSELL_STARTS_AT,
    endAt: BTM_MN_S1_EVENT_END_AT,
    games: ['wowretail']
  },
  {
    id: 'mdi-replay-race-midnight-season-1',
    startAt: MDI_REPLAY_RACE_MN_S1_STARTS_AT,
    endAt: MDI_REPLAY_RACE_MN_S1_ENDS_AT,
    games: ['wowretail']
  },
  {
    id: 'mdi-midnight-season-finals',
    startAt: MDI_MIDNIGHT_SEASON_FINALS_STARTS_AT,
    endAt: MDI_MIDNIGHT_SEASON_FINALS_ENDS_AT,
    games: ['wowretail']
  },
  {
    id: 'elekk-giveaway',
    startAt: ELEKK_GIVEAWAY_STARTS_AT,
    endAt: ELEKK_GIVEAWAY_ENDS_AT
  },
  {
    id: 'awc-midnight-cup-3',
    startAt: AWC_MIDNIGHT_GRAND_FINALS_STARTS_AT,
    endAt: AWC_MIDNIGHT_GRAND_FINALS_ENDS_AT
  },
  {
    id: 'mdi-midnight-groups',
    startAt: MDI_MIDNIGHT_GROUP_A_STARTS_AT,
    endAt: MDI_MIDNIGHT_GROUP_A_ENDS_AT
  },
  {
    id: 'mdi-midnight-groups',
    startAt: MDI_MIDNIGHT_GROUP_B_STARTS_AT,
    endAt: MDI_MIDNIGHT_GROUP_B_ENDS_AT
  },
  {
    id: 'mdi-midnight-groups',
    startAt: MDI_MIDNIGHT_GROUP_C_STARTS_AT,
    endAt: MDI_MIDNIGHT_GROUP_C_ENDS_AT
  },
  {
    id: 'midnight-addons',
    startAt: MIDNIGHT_TIME_TRIALS_ENDS_AT,
    endAt: '2026-04-21T22:00:00Z'
  },
  {
    id: 'most-of-rio-midnight',
    startAt: MIDNIGHT_TIME_TRIALS_ENDS_AT,
    endAt: '2026-04-21T22:00:00Z'
  },
  {
    id: 'midnight-affixes',
    startAt: MIDNIGHT_TIME_TRIALS_ENDS_AT,
    endAt: '2026-04-21T22:00:00Z'
  }
];

export function getNextUpsellScheduleBoundary(game: TGameSite, now: Date): Date | null {
  const nowTime = now.getTime();
  const boundaries = ACTIVE_SHARED_UPSELLS.flatMap((upsell) => {
    if (upsell.games && !upsell.games.includes(game)) {
      return [];
    }

    const startAt = upsell.startAt ? Date.parse(upsell.startAt) : null;
    const endAt = upsell.endAt ? Date.parse(upsell.endAt) + 1 : null;

    return [startAt, endAt].filter((boundary): boundary is number => boundary !== null && boundary > nowTime);
  });

  if (boundaries.length === 0) {
    return null;
  }

  return new Date(Math.min(...boundaries));
}

function isUpsellActive(upsell: TUpsellConfig, game: TGameSite, now: Date): boolean {
  if (upsell.games && !upsell.games.includes(game)) {
    return false;
  }

  if (upsell.startAt) {
    const startAt = new Date(upsell.startAt);
    if (now < startAt) {
      return false;
    }
  }

  if (upsell.endAt) {
    const endAt = new Date(upsell.endAt);
    if (now > endAt) {
      return false;
    }
  }

  return true;
}

export function resolveUpsellQualifications(runtime: TUpsellQualificationRuntime): TUpsellQualifications {
  const qualifications = new Map<TUpsellId, unknown>();

  for (const upsell of ACTIVE_SHARED_UPSELLS) {
    if (!upsell.qualify || qualifications.has(upsell.id)) {
      continue;
    }

    const qualification = upsell.qualify(runtime);
    if (qualification !== null) {
      qualifications.set(upsell.id, qualification);
    }
  }

  return qualifications;
}

function isUpsellQualified(upsell: TUpsellConfig, qualifications?: TUpsellQualifications): boolean {
  if (!upsell.qualify) {
    return true;
  }

  return qualifications?.has(upsell.id) === true;
}

type TSelectUpsellIdOptions = {
  game: TGameSite;
  now: Date;
  qualifications?: TUpsellQualifications;
  seed: number;
  sequenceIndex?: number;
};

/**
 * Rotates the eligible slot list by seed, then walks it into the page's sequence of unique
 * upsell ids. Weighted upsells occupy multiple slots, raising their odds of the first
 * position without ever appearing twice on one page.
 */
export function selectUpsellIdFromSequence(
  eligibleUpsellSlots: readonly TUpsellId[],
  seed: number,
  sequenceIndex = 0
): TUpsellId | null {
  if (eligibleUpsellSlots.length === 0 || sequenceIndex < 0) {
    return null;
  }

  const slotCount = eligibleUpsellSlots.length;
  const initialIndex = ((seed % slotCount) + slotCount) % slotCount;
  const sequence: TUpsellId[] = [];
  const seenUpsellIds = new Set<TUpsellId>();
  for (let slotOffset = 0; slotOffset < slotCount; slotOffset++) {
    const upsellId = eligibleUpsellSlots[(initialIndex + slotOffset) % slotCount];
    if (!seenUpsellIds.has(upsellId)) {
      seenUpsellIds.add(upsellId);
      sequence.push(upsellId);
    }
  }

  return sequenceIndex < sequence.length ? sequence[sequenceIndex] : null;
}

export function selectUpsellId(options: TSelectUpsellIdOptions): TUpsellId | null {
  const seenUpsellIds = new Set<TUpsellId>();
  const eligibleUpsellSlots = ACTIVE_SHARED_UPSELLS.filter(
    (upsell) => isUpsellActive(upsell, options.game, options.now) && isUpsellQualified(upsell, options.qualifications)
  ).flatMap((upsell) => {
    if (seenUpsellIds.has(upsell.id)) {
      return [];
    }
    seenUpsellIds.add(upsell.id);
    const weight = Math.max(1, Math.floor(upsell.weight ?? 1));
    return Array.from({ length: weight }, () => upsell.id);
  });

  return selectUpsellIdFromSequence(eligibleUpsellSlots, options.seed, options.sequenceIndex);
}
